import { useMemo } from 'react';
import { Button } from '@coordio/ui';
import { Download as DownloadIcon } from 'lucide-react';
import { fileService } from '../../services/fileService';
import { tryParseMediaProcessingResult } from '../../types/multimodal';
import { MediaProcessingStatus } from './MediaProcessingStatus';

export interface DocumentMessageProps {
  fileId: string;
  processedContentJson?: string | null;
}

/**
 * Renders a document attachment with extracted text (if available).
 */
export const DocumentMessage = ({ fileId, processedContentJson }: DocumentMessageProps) => {
  const result = useMemo(() => tryParseMediaProcessingResult(processedContentJson), [processedContentJson]);

  const download = async () => {
    const resp = await fileService.downloadFileBlob(fileId);
    const blob = resp.data;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attachment-${fileId}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-sm font-medium">Document</span>
        <MediaProcessingStatus processedContentJson={processedContentJson} />
        <Button size="sm" variant="outline" onClick={download}>
          <DownloadIcon className="mr-1 h-3 w-3" />
          Download
        </Button>
      </div>

      {result?.caption && (
        <p className="text-sm mt-2 whitespace-pre-wrap">
          {result.caption}
        </p>
      )}

      {result?.extractedText && (
        <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">
          {result.extractedText}
        </p>
      )}
    </div>
  );
};
